import { useCreateCollectionAndAddFeedback } from '@modules/feedback/hooks';
import { trackCollectionEvents } from '@modules/savedCollection/utils';
import { AddRounded } from '@mui/icons-material';
import { Button, ButtonProps, Paper } from '@mui/material';
import { SyntheticEvent, useState } from 'react';
import { WhereClause } from 'v2/core/services/queryBuilder/whereClause';
import { AddToNewCollectionDialog } from '../AddToNewCollectionDialog';

interface AddToNewCollectionButtonProps {
  feedbackID: string;
  onMenuClose: (event: SyntheticEvent) => void;
  buttonProps?: {
    label?: string;
    startIcon?: JSX.Element;
    variant?: ButtonProps['variant'];
  };
  initialWhereClause?: WhereClause;
}

export const AddToNewCollectionButton = ({
  feedbackID,
  onMenuClose,
  buttonProps = {},
  initialWhereClause,
}: AddToNewCollectionButtonProps) => {
  const [isAddToNewCollectionDialogOpen, setIsAddToNewCollectionDialogOpen] =
    useState(false);
  const [nameOfCollection, setNameOfCollection] = useState('');
  const [descriptionOfCollection, setDescriptionOfCollection] = useState('');

  const onAddToNewCollectionDialogClose = (event: SyntheticEvent) => {
    setIsAddToNewCollectionDialogOpen(false);
    onMenuClose(event);
  };

  const { createCollectionAndAddFeedback, isLoading } =
    useCreateCollectionAndAddFeedback({
      nameOfCollection,
      descriptionOfCollection,
      feedbackID: feedbackID,
      initialWhereClause,
    });

  const onCreateNewCollection = async (
    event?: SyntheticEvent | KeyboardEvent,
  ) => {
    event?.stopPropagation();
    event?.preventDefault();
    await createCollectionAndAddFeedback();
    // typecasting to fix the lack of properties in KeyboardEvent which we are not using
    event && onAddToNewCollectionDialogClose(event as SyntheticEvent);

    trackCollectionEvents({
      eventName: 'Create New Collection',
      name: nameOfCollection,
      description: descriptionOfCollection,
    });
  };

  return (
    <Paper elevation={12}>
      <Button
        startIcon={buttonProps?.startIcon ?? <AddRounded />}
        onClick={(event) => {
          event.stopPropagation();
          setIsAddToNewCollectionDialogOpen(true);
        }}
        fullWidth
        variant={buttonProps?.variant ?? 'text'}
        size="medium"
        sx={{
          textAlign: 'start',
          justifyContent: 'flex-start',
        }}
      >
        {buttonProps?.label ?? 'Add to a new Collection'}
      </Button>

      {isAddToNewCollectionDialogOpen && (
        <AddToNewCollectionDialog
          onClose={onAddToNewCollectionDialogClose}
          onCreateNewCollection={onCreateNewCollection}
          isAddingInProgress={isLoading}
          nameOfCollection={nameOfCollection}
          setNameOfCollection={setNameOfCollection}
          descriptionOfCollection={descriptionOfCollection}
          setDescriptionOfCollection={setDescriptionOfCollection}
        />
      )}
    </Paper>
  );
};
